/* eslint-disable max-lines */
import { useContext } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { Context } from './contexts/AuthContext';
import { buildLoginPathWithRedirect } from './helpers/safeRedirect';
import LoaderSpinner from './components/Core/LoaderSpinner';
import AuthLayout from './layouts/Auth';
import DefaultLayout from './layouts/Logged';
import Login from './views/pages/Auth/Login';
import Home from './views/Home';
import FranchiseeList from './views/pages/Franchisee/FranchiseeList';
import FranchiseeCreate from './views/pages/Franchisee/FranchiseeCreate';
import FranchiseeIntegrationConfig from './views/pages/Franchisee/FranchiseeIntegrationConfig';
import NotFound from './views/pages/NotFound';
import Profile from './views/pages/Users/Profile';
import SupportList from './views/pages/Support/SupportList';
import SupportNew from './views/pages/Support/SupportNew';
import SupportView from './views/pages/Support/SupportView';
import QrCode from './views/pages/QrCode';
import SchoolList from './views/pages/School/SchoolList';
import SchoolCreate from './views/pages/School/SchoolCreate';
import UserList from './views/pages/Users/UserList';
import ClassList from './views/pages/Class/ClassList';
import ClassDetailedList from './views/pages/Class/ClassDetailedList';
import ClassCreate from './views/pages/Class/ClassCreate';
import ClassManage from './views/pages/Class/ClassManage';
import ClassInfo from './views/pages/Class/ClassInfo';
import UserCreate from './views/pages/Users/UserCreate';
import UserProfile from './views/pages/Users/UserProfile';
import KidsList from './views/pages/Kids/KidsList';
import KidsCreate from './views/pages/Kids/KidsCreate';
import TimelineList from './views/pages/Timeline/TimelineKids';
import DocumentsFoldersList from './views/pages/Documents/DocumentsFoldersList';
import DocumentsPdfShow from './views/pages/Documents/DocumentsPdfShow';
import MessagingHome from './views/pages/Messaging/MessagingHome';
import SettingsList from './views/pages/Settings/SettingsList';
import SchoolScheduleEventImportForm from './views/pages/SchoolScheduleEvent/SchoolScheduleEventImportForm';
import SchoolScheduleEventImportList from './views/pages/SchoolScheduleEvent/SchoolScheduleEventImportList';

import TestTransaction from './views/pages/Transactions/TestTransaction';
import BillingList from './views/pages/Billing/BillingList';
import Billing from './views/pages/Billing/Billing';
import Register from './views/pages/Auth/Register';
import ForgotPassword from './views/pages/Auth/ForgotPassword';
import Reports from './views/pages/Reports';
import StudentProfile from './views/pages/Kids/StudentProfile';
import AdminSignature from './views/pages/IncidentReport/AdminSignature';
import DirectoryList, { ShowModalModes } from './views/pages/Staff/DirectoryList';
import Pto from './views/pages/Staff/Pto/Pto';
import PtoPolicesForm from './views/pages/Users/PtoPolicesForm';
import LoaPolicesForm from './views/pages/Users/LoaPolicesForm';
import Payroll from './views/pages/Staff/Payroll';
import PerformanceManagement from './views/pages/Staff/PerformanceManagement';
import { canViewPerformanceManagement } from './helpers/performanceManagementPermissions';
import hrHomePath from './helpers/hrHomePath';

import PromoCodesList from './views/pages/PromoCodes/PromoCodesList';
import PromoCodesCreate from './views/pages/PromoCodes/PromoCodesCreate';
import NewHireAccept from './views/pages/NewHire/NewHireAccept';
import NewHireReject from './views/pages/NewHire/NewHireReject';
import ForgotPasswordReset from './views/pages/Auth/ForgotPasswordReset';

import WaitingListForm from './views/pages/WaitingListPublic/WaitingListForm';
import TourScheduleForm from './views/pages/TourPublic/TourScheduleForm';
import EnrollForm from './views/pages/EnrollPublic/EnrollForm';

import ParentsDocumentsList from './views/pages/Documents/ParentsDocumentsList';

import EmployeeSettingsView from './views/pages/Users/EmployeeSettingsView';
import NewHireDocuments from './views/pages/NewHire/NewHireDocuments';
import NewHireDocumentsV2 from './views/pages/NewHire/v2/NewHireDocumentsV2';
import AnnualReviewFlow from './views/pages/AnnualReview/AnnualReviewFlow';
import SupervisorAnnualReviewFlow from './views/pages/AnnualReview/SupervisorFlow';

import ImportIndex from './views/pages/Import/Index';
import StartConfiguration from './views/pages/Home/StartConfiguration';
import Logout from './views/pages/Auth/Logout';
import HireAgreementsNew from './views/pages/Users/HireAgreementsNew';
import TaskBoardRedirect from './views/pages/TaskBoard/TaskBoardRedirect';
import TaskBoardPage from './views/pages/TaskBoard/TaskBoardPage';
import TeacherTicketPage from './views/pages/TaskBoard/TeacherTicketPage';
import DailySchedulePage from './views/pages/DailySchedule/DailySchedulePage';
import WeeklySchedulePage from './views/pages/DailySchedule/WeeklySchedulePage';

const CustomRoute = ({ children, isPrivate, ...rest }: any) => {
  const { loading, authenticated, user }: any = useContext(Context);
  const location = useLocation();

  if (loading) {
    return <LoaderSpinner />;
  }

  if (authenticated && !user?.createdAt) {
    return <LoaderSpinner />;
  }

  if (rest.redirectTo) {
    return <Redirect to={rest.redirectTo} />;
  }

  if (isPrivate && !authenticated) {
    return <Redirect to={buildLoginPathWithRedirect(`${location?.pathname || ''}${location?.search || ''}`)} />;
  }

  const Layout = isPrivate && rest.path !== '/initial-config' ? DefaultLayout : AuthLayout;
  return <Route {...rest} render={() => <Layout>{children}</Layout>} />;
};

const PerformanceManagementRoute = () => {
  const { user }: any = useContext(Context);
  if (!canViewPerformanceManagement(user)) {
    return <Redirect to="/home" />;
  }
  return <PerformanceManagement />;
};

const ChildcareRoute = ({ children }: any) => {
  const { childcareEnabled, childcareResolved }: any = useContext(Context);
  if (!childcareResolved) {
    return <LoaderSpinner />;
  }
  if (!childcareEnabled) {
    return <Redirect to="/home" />;
  }
  return children;
};

export default function Routes() {
  const { user }: any = useContext(Context);
  const currentAppVersion = localStorage.getItem('x-access-app-version') || 'HR';
  return (
    <Switch>
      <CustomRoute exact path="/" redirectTo="/home"></CustomRoute>
      <CustomRoute
        isPrivate
        exact
        path="/home"
        redirectTo={
          currentAppVersion === 'HR' || ['HRUser', 'FinanceUser'].includes(user?.group?.name)
            ? hrHomePath(user)
            : undefined
        }
      >
        <Home />
      </CustomRoute>
      <CustomRoute exact path="/login">
        <Login />
      </CustomRoute>
      <Route exact path="/logout">
        <Logout />
      </Route>
      <CustomRoute exact path="/register">
        <Register />
      </CustomRoute>
      <CustomRoute exact path="/forgot-password">
        <ForgotPassword />
      </CustomRoute>
      <CustomRoute exact path="/new-hire/accept/:token">
        <NewHireAccept />
      </CustomRoute>
      <CustomRoute exact path="/new-hire/reject/:token">
        <NewHireReject />
      </CustomRoute>
      <Route exact path="/new-hire/documents/:token">
        <NewHireDocumentsV2 />
      </Route>
      <Route exact path="/annual-review/:token">
        <AnnualReviewFlow />
      </Route>
      <CustomRoute isPrivate exact path="/staff/annual-reviews/:id">
        <SupervisorAnnualReviewFlow />
      </CustomRoute>
      <Route exact path="/new-hire/documents-legacy/:token">
        <NewHireDocuments />
      </Route>
      <CustomRoute exact path="/forgot-password/:token">
        <ForgotPasswordReset />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/franchisee">
        <FranchiseeList />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/franchisee/new">
        <FranchiseeCreate />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/franchisee/:id/integration">
        <FranchiseeIntegrationConfig />
      </CustomRoute>
      <CustomRoute isPrivate path="/franchisee/:id">
        <FranchiseeCreate />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/profile">
        <Profile />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/support">
        <SupportList />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/support/new">
        <SupportNew />
      </CustomRoute>
      <CustomRoute isPrivate path="/incident-report/:id">
        <AdminSignature />
      </CustomRoute>
      <CustomRoute isPrivate path="/support/:id">
        <SupportView />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/class">
        <ChildcareRoute>
          <ClassDetailedList />
        </ChildcareRoute>
      </CustomRoute>
      <CustomRoute exact path="/class-info/:id">
        <ClassInfo />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/class/list">
        <ChildcareRoute>
          <ClassList />
        </ChildcareRoute>
      </CustomRoute>
      <CustomRoute isPrivate exact path="/class/new">
        <ChildcareRoute>
          <ClassCreate />
        </ChildcareRoute>
      </CustomRoute>
      <CustomRoute isPrivate path="/class/:id/details">
        <ChildcareRoute>
          <ClassManage />
        </ChildcareRoute>
      </CustomRoute>
      <CustomRoute isPrivate path="/class/:id">
        <ChildcareRoute>
          <ClassCreate />
        </ChildcareRoute>
      </CustomRoute>
      <CustomRoute isPrivate exact path="/qrcode">
        <QrCode />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/school">
        <SchoolList />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/school/new">
        <SchoolCreate />
      </CustomRoute>
      <CustomRoute isPrivate path="/school/:id">
        <SchoolCreate />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/users">
        <UserList />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/users/new">
        <UserCreate />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/users/:id/profile/:tabToOpen?">
        <UserProfile />
      </CustomRoute>
      <CustomRoute isPrivate path="/users/:id/profile">
        <UserProfile />
      </CustomRoute>
      <CustomRoute isPrivate path="/pto-policies-settings/:id?">
        <PtoPolicesForm />
      </CustomRoute>
      <CustomRoute isPrivate path="/loa-policies-settings/:id?">
        <LoaPolicesForm />
      </CustomRoute>
      <CustomRoute isPrivate path="/employee-settings">
        <EmployeeSettingsView />
      </CustomRoute>
      <CustomRoute isPrivate path="/hire-agreements/new">
        <HireAgreementsNew />
      </CustomRoute>
      <CustomRoute isPrivate path="/hire-agreements/assign/:id">
        <HireAgreementsNew />
      </CustomRoute>
      <CustomRoute isPrivate path="/hire-agreements/:id/edit">
        <HireAgreementsNew />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/student-profile/:id">
        <ChildcareRoute>
          <StudentProfile />
        </ChildcareRoute>
      </CustomRoute>
      <CustomRoute isPrivate path="/users/:id">
        <UserCreate />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/directory">
        <DirectoryList showModal={undefined} />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/directory/denieds/:id/reason">
        <DirectoryList showModal={ShowModalModes.DeniedReason} />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/directory/approvals/new-hire">
        <DirectoryList showModal={ShowModalModes.NewHireApproval} />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/directory/approvals/raise">
        <DirectoryList showModal={ShowModalModes.RaiseApproval} />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/pto">
        <Pto />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/pto/approvals">
        <Pto showModal={true} />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/payroll">
        <Payroll />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/performance-management">
        <PerformanceManagementRoute />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/teacher-ticket">
        <TeacherTicketPage />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/task-board">
        <TaskBoardRedirect />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/task-board/:schoolId">
        <TaskBoardPage />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/schedule">
        <DailySchedulePage />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/schedule/weekly">
        <WeeklySchedulePage />
      </CustomRoute>
      <CustomRoute isPrivate exact path="/kids">
        <ChildcareRoute>
          <KidsList />
        </ChildcareRoute>
      </CustomRoute>
      <CustomRoute isPrivate exact path="/kids/new">
        <ChildcareRoute>
          <KidsCreate />
        </ChildcareRoute>
      </CustomRoute>
      <CustomRoute isPrivate path="/kids/:id">
        <ChildcareRoute>
          <KidsCreate />
        </ChildcareRoute>
      </CustomRoute>
      {/* <CustomRoute isPrivate exact path="/schedule">
        <ScheduleCalendar />
      </CustomRoute> */}
      <CustomRoute isPrivate exact path="/timeline/:kidsId">
        <ChildcareRoute>
          <TimelineList />
        </ChildcareRoute>
      </CustomRoute>
      <CustomRoute isPrivate exact path="/curriculum">
        <ChildcareRoute>
          <DocumentsFoldersList />
        </ChildcareRoute>
      </CustomRoute>
      <CustomRoute isPrivate path="/curriculum/:id">
        <ChildcareRoute>
          <DocumentsFoldersList />
        </ChildcareRoute>
      </CustomRoute>
      <CustomRoute isPrivate exact path="/resource">
        <DocumentsFoldersList />
      </CustomRoute>
      <CustomRoute isPrivate path="/resource/:id">
        <DocumentsFoldersList />
      </CustomRoute>

      <CustomRoute isPrivate exact path="/messaging">
        <ChildcareRoute>
          <MessagingHome />
        </ChildcareRoute>
      </CustomRoute>

      <CustomRoute isPrivate exact path="/settings">
        <SettingsList />
      </CustomRoute>

      <CustomRoute isPrivate exact path="/reports">
        <Reports />
      </CustomRoute>

      <CustomRoute isPrivate exact path="/test-transaction">
        <TestTransaction />
      </CustomRoute>

      <CustomRoute isPrivate exact path="/billing">
        <ChildcareRoute>
          <BillingList />
        </ChildcareRoute>
      </CustomRoute>

      <CustomRoute isPrivate exact path="/billing/:kidsId">
        <ChildcareRoute>
          <Billing />
        </ChildcareRoute>
      </CustomRoute>

      <CustomRoute isPrivate exact path="/billing-parents/:parentId">
        <ChildcareRoute>
          <Billing />
        </ChildcareRoute>
      </CustomRoute>

      <CustomRoute isPrivate exact path="/monthly-info">
        <ChildcareRoute>
          <SchoolScheduleEventImportList />
        </ChildcareRoute>
      </CustomRoute>

      <CustomRoute isPrivate exact path="/monthly-info/import">
        <ChildcareRoute>
          <SchoolScheduleEventImportForm />
        </ChildcareRoute>
      </CustomRoute>

      <CustomRoute isPrivate exact path="/promo-codes">
        <PromoCodesList />
      </CustomRoute>

      <CustomRoute isPrivate exact path="/promo-codes/new">
        <PromoCodesCreate />
      </CustomRoute>

      <CustomRoute isPrivate path="/promo-codes/:id">
        <PromoCodesCreate />
      </CustomRoute>

      <CustomRoute isPrivate exact path="/imports">
        <ImportIndex />
      </CustomRoute>

      <CustomRoute isPrivate path="/view-pdf/:id">
        <DocumentsPdfShow />
      </CustomRoute>

      <CustomRoute exact path="/waiting-list/:schoolUri">
        <WaitingListForm />
      </CustomRoute>

      <CustomRoute exact path="/tour/:franchiseeId/:schoolUri?">
        <TourScheduleForm />
      </CustomRoute>

      <CustomRoute exact path="/tour/:franchiseeId/:schoolUri/schedule/:scheduleId">
        <TourScheduleForm />
      </CustomRoute>

      <CustomRoute exact path="/enroll/:schoolUri">
        <EnrollForm />
      </CustomRoute>

      <CustomRoute isPrivate exact path="/documents">
        <ParentsDocumentsList />
      </CustomRoute>

      <CustomRoute isPrivate exact path="/initial-config">
        <StartConfiguration />
      </CustomRoute>

      <Route component={NotFound} />
    </Switch>
  );
}
